import clsx from 'clsx';
import React from 'react';

import Paywall from '@components/Paywall';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { ContentType, Page } from '@tracking';

import Feature from './Layouts/Feature';
import Standard from './Layouts/Standard';
import Weekend from './Layouts/Weekend';
import { ArticleProps, ArticleTheme, ThemeViewsProps } from './interfaces';

const getArticleTemplate = (theme: string) => {
  switch (theme) {
    case ArticleTheme.Feature:
      return Feature;
    case ArticleTheme.Weekend:
      return Weekend;
    default:
      return Standard;
  }
};

const ArticleLayout = ({
  article,
  canViewFullArticle,
  clipperProps,
  env,
  isFullyFreeArticle,
  isGooglebot,
  isWebview,
  mostPopularArticles,
  newsletter,
  paywallProps,
  relatedArticles,
  shareLink,
  subscriberCouncil,
  theme,
}: ArticleProps) => {
  const hydrated = useServerSideRendering();
  const themeProps: ThemeViewsProps = {
    article,
    canViewFullArticle,
    clipperProps,
    isFullyFreeArticle,
    isGooglebot,
    env,
    mostPopularArticles,
    newsletter,
    relatedArticles,
    shareLink,
    subscriberCouncil,
  };
  const ArticleTemplate = getArticleTemplate(theme);
  const { Track } = useEventTracking({
    page: Page.Article,
    contentType: ContentType.Article,
    contentId: article.id,
  });

  return (
    <Track>
      <article
        className={clsx('relative pb-64 sm:pb-112', {
          'pt-32 sm:pt-48': !isWebview,
        })}
      >
        {!canViewFullArticle && (
          <div
            className="absolute bottom-0 left-0 top-0 z-40 w-full bg-black/30"
            data-testid="paywall-overlay"
          />
        )}
        <ArticleTemplate {...themeProps} />
      </article>
      {(!canViewFullArticle || isFullyFreeArticle) && paywallProps?.title && (
        <>
          {hydrated && (
            <Paywall
              articleId={article.id}
              isPro={article.isPro}
              trackingProps={{ 'track-event': 'Paywall' }}
              {...paywallProps}
            />
          )}
        </>
      )}
    </Track>
  );
};

export default ArticleLayout;
